import React, { useState, useEffect, useCallback, useRef, useMemo, startTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  WrapText as WrapTextIcon,
  ContentCopy as CopyIcon,
  ArrowBack as PreviousIcon,
  ArrowForward as NextIcon,
  FormatListNumbered as LineNumbersIcon
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useToast } from '../../context/ToastContext';
import config from '../../utils/config';
import CopyableText from '../common/CopyableText';

// Separate search input component to isolate re-renders
interface SearchInputProps {
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

const SearchInput = React.memo<SearchInputProps>(({ onSearchChange, onRefresh, isRefreshing }) => {
  const [localValue, setLocalValue] = useState('');

  // Debounce and notify parent
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [localValue, onSearchChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  return (
    <TextField
      fullWidth
      size="small"
      placeholder="Search keywords..."
      value={localValue}
      onChange={handleChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Refresh results" arrow>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={isRefreshing}
                edge="end"
              >
                {isRefreshing ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        )
      }}
    />
  );
});

SearchInput.displayName = 'SearchInput';

interface BatchResult {
  id: number;
  keyword: string;
  status: string;
}

interface BatchResultDetail {
  id: number;
  keyword: string;
  status: string;
  result?: any;
  error_message?: string;
  updated_at?: string;
}

interface BatchResultsDialogProps {
  open: boolean;
  onClose: () => void;
  batchId: string;
  initialFilter?: 'all' | 'completed' | 'failed';
}

const BatchResultsDialog: React.FC<BatchResultsDialogProps> = ({
  open,
  onClose,
  batchId,
  initialFilter = 'all'
}) => {
  const { showToast } = useToast();
  const [allResults, setAllResults] = useState<BatchResult[]>([]); // All keywords metadata
  const [filteredResults, setFilteredResults] = useState<BatchResult[]>([]); // Filtered by search/status
  const [selectedResult, setSelectedResult] = useState<BatchResult | null>(null);
  const [selectedResultDetail, setSelectedResultDetail] = useState<BatchResultDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'failed'>(initialFilter);
  const [lineWrap, setLineWrap] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  
  // Use ref for abort controller
  const abortControllerRef = useRef<AbortController | null>(null);

  // Reset filter when dialog opens or initialFilter changes
  useEffect(() => {
    if (open) {
      setStatusFilter(initialFilter);
      setPage(1);
    }
  }, [open, initialFilter]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  // Fetch ALL keywords metadata (without results_data) once
  useEffect(() => {
    if (!open || !batchId) return;

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchAllResults = async () => {
      setIsLoading(true);

      try {
        const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-results/${batchId}`;
        const response = await fetch(apiUrl, { signal: abortController.signal });

        if (response.ok) {
          const data = await response.json();
          
          // Use startTransition to make this update non-blocking
          // This keeps UI responsive while React processes the large dataset
          startTransition(() => {
            setAllResults(data || []);
            setIsLoading(false);
          });
        } else {
          showToast('Failed to fetch batch results', 'error');
          setIsLoading(false);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching batch results:', error);
          showToast('Failed to fetch batch results', 'error');
        }
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchAllResults();

    return () => {
      abortController.abort();
    };
  }, [open, batchId]); // showToast is stable, don't include it

  // Client-side filtering based on search and status - optimized for large datasets
  useEffect(() => {
    // Use startTransition to keep filtering responsive with large datasets
    startTransition(() => {
      const searchLower = searchQuery.toLowerCase();
      
      // Single-pass filtering for better performance
      const filtered = allResults.filter(r => {
        // Check status filter
        if (statusFilter !== 'all' && r.status !== statusFilter) {
          return false;
        }
        
        // Check search filter
        if (searchQuery && !r.keyword.toLowerCase().includes(searchLower)) {
          return false;
        }
        
        return true;
      });

      setFilteredResults(filtered);
      
      // Auto-select first result if nothing selected
      if (filtered.length > 0 && !selectedResult) {
        setSelectedResult(filtered[0]);
      }
    });
  }, [allResults, statusFilter, searchQuery, selectedResult]);

  // Load result detail when a keyword is selected
  useEffect(() => {
    if (!selectedResult) {
      setSelectedResultDetail(null);
      return;
    }

    const fetchResultDetail = async () => {
      // Only show loading indicator, don't clear previous content
      setIsLoadingDetail(true);

      try {
        // Pass batch_id as query param so backend knows which table to query (guardrail vs snowflake)
        const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-result-data/${selectedResult.id}?batch_id=${encodeURIComponent(batchId)}`;
        const response = await fetch(apiUrl);

        if (response.ok) {
          const data = await response.json();
          // Use startTransition for smooth update without flickering
          startTransition(() => {
            setSelectedResultDetail(data);
            setIsLoadingDetail(false);
          });
        } else {
          showToast('Failed to fetch result details', 'error');
          setIsLoadingDetail(false);
        }
      } catch (error) {
        console.error('Error fetching result details:', error);
        showToast('Failed to fetch result details', 'error');
        setIsLoadingDetail(false);
      }
    };

    fetchResultDetail();
  }, [selectedResult]); // showToast is stable, don't include it

  // Manual refresh handler
  const handleRefresh = async () => {
    if (!open || !batchId || isRefreshing) return;

    setIsRefreshing(true);

    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-results/${batchId}`;
      const response = await fetch(apiUrl);

      if (response.ok) {
        const data = await response.json();
        setAllResults(data || []);
        showToast('Results refreshed', 'success');
      } else {
        showToast('Failed to refresh batch results', 'error');
      }
    } catch (error) {
      console.error('Error refreshing batch results:', error);
      showToast('Failed to refresh batch results', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Copy result to clipboard
  const handleCopyResult = () => {
    if (!selectedResultDetail?.result) return;

    let textToCopy = '';
    if (typeof selectedResultDetail.result === 'object') {
      textToCopy = JSON.stringify(selectedResultDetail.result, null, 2);
    } else if (typeof selectedResultDetail.result === 'string') {
      try {
        const parsed = JSON.parse(selectedResultDetail.result);
        textToCopy = JSON.stringify(parsed, null, 2);
      } catch {
        textToCopy = selectedResultDetail.result;
      }
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      showToast('Result copied to clipboard', 'success');
    }).catch(() => {
      showToast('Failed to copy to clipboard', 'error');
    });
  };

  // Calculate paginated results for display - memoized for performance
  const paginatedResults = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredResults.slice(startIndex, startIndex + pageSize);
  }, [filteredResults, page, pageSize]);

  const totalPages = useMemo(() => 
    Math.ceil(filteredResults.length / pageSize), 
    [filteredResults.length, pageSize]
  );
  
  const hasMore = useMemo(() => 
    page < totalPages, 
    [page, totalPages]
  );

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setPage(page + 1);
    }
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = event.target.value as number;
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleKeywordClick = (result: BatchResult) => {
    setSelectedResult(result);
  };

  const handleStatusFilterChange = (_event: React.SyntheticEvent, newValue: 'all' | 'completed' | 'failed') => {
    setStatusFilter(newValue);
    setPage(1); // Reset to first page when filter changes
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleClose = () => {
    setSearchQuery('');
    setStatusFilter(initialFilter);
    setPage(1);
    setPageSize(20);
    setAllResults([]);
    setFilteredResults([]);
    setSelectedResult(null);
    setSelectedResultDetail(null);
    onClose();
  };

  const renderResultDetail = () => {
    if (!selectedResult) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: 'text.secondary'
          }}
        >
          <Typography>Select a keyword to view details</Typography>
        </Box>
      );
    }

    if (selectedResult.status === 'failed') {
      // Parse result if it's a string
      let resultData = selectedResultDetail?.result;
      if (typeof resultData === 'string') {
        try {
          resultData = JSON.parse(resultData);
        } catch (e) {
          // Keep as string if not valid JSON
        }
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Keyword Header with Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {selectedResult.keyword}
            </Typography>
            {selectedResultDetail?.result && (
              <>
                <Tooltip title={lineWrap ? "Disable line wrap" : "Enable line wrap"} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setLineWrap(!lineWrap)}
                    color={lineWrap ? "primary" : "default"}
                  >
                    <WrapTextIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={showLineNumbers ? "Hide line numbers" : "Show line numbers"} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowLineNumbers(!showLineNumbers)}
                    color={showLineNumbers ? "primary" : "default"}
                  >
                    <LineNumbersIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy to clipboard" arrow>
                  <IconButton
                    size="small"
                    onClick={handleCopyResult}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          
          {/* Error Message */}
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Error Processing Keyword
            </Typography>
            {selectedResultDetail?.error_message || 'Loading error details...'}
          </Alert>

          {/* Result Data Section - takes remaining space */}
          {selectedResultDetail?.result && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Result Data
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  '& pre': {
                    height: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }
                }}
              >
                <SyntaxHighlighter
                  language="json"
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    paddingLeft: showLineNumbers ? '0.5rem' : '1rem'
                  }}
                  showLineNumbers={showLineNumbers}
                  lineNumberStyle={{
                    minWidth: '2em',
                    paddingRight: '0.75em',
                    textAlign: 'right',
                    userSelect: 'none'
                  }}
                  codeTagProps={{
                    style: {
                      ...(lineWrap && { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }),
                    }
                  }}
                >
                  {typeof resultData === 'object' 
                    ? JSON.stringify(resultData, null, 2)
                    : resultData}
                </SyntaxHighlighter>
              </Box>
            </Box>
          )}
          
          {/* Timestamp */}
          {selectedResultDetail?.updated_at && (
            <Box sx={{ pt: 2, borderTop: selectedResultDetail?.result ? 1 : 0, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Processed: {new Date(selectedResultDetail?.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    if (!selectedResultDetail?.result) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Keyword Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {selectedResult.keyword}
            </Typography>
          </Box>
          
          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              No result data available for this keyword.
            </Typography>
          </Box>
          
          {/* Timestamp */}
          {selectedResultDetail?.updated_at && (
            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Processed: {new Date(selectedResultDetail?.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Parse result if it's a string
    let resultData = selectedResultDetail.result;
    if (typeof resultData === 'string') {
      try {
        resultData = JSON.parse(resultData);
      } catch (e) {
        // Keep as string if not valid JSON
      }
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Keyword Header with Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
          <SuccessIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {selectedResult.keyword}
          </Typography>
          <Tooltip title={lineWrap ? "Disable line wrap" : "Enable line wrap"} arrow>
            <IconButton
              size="small"
              onClick={() => setLineWrap(!lineWrap)}
              color={lineWrap ? "primary" : "default"}
            >
              <WrapTextIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showLineNumbers ? "Hide line numbers" : "Show line numbers"} arrow>
            <IconButton
              size="small"
              onClick={() => setShowLineNumbers(!showLineNumbers)}
              color={showLineNumbers ? "primary" : "default"}
            >
              <LineNumbersIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy to clipboard" arrow>
            <IconButton
              size="small"
              onClick={handleCopyResult}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Result Display - takes remaining space */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            '& pre': {
              height: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }
          }}
        >
            <SyntaxHighlighter
              language="json"
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                borderRadius: '4px',
                fontSize: '0.85rem',
                paddingLeft: showLineNumbers ? '0.5rem' : '1rem'
              }}
              showLineNumbers={showLineNumbers}
              lineNumberStyle={{
                minWidth: '2em',
                paddingRight: '0.75em',
                textAlign: 'right',
                userSelect: 'none'
              }}
              codeTagProps={{
                style: {
                  ...(lineWrap && { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }),
                }
              }}
            >
              {typeof resultData === 'object' 
              ? JSON.stringify(resultData, null, 2)
              : resultData}
            </SyntaxHighlighter>
        </Box>

        {/* Timestamp */}
        {selectedResultDetail?.updated_at && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Processed: {new Date(selectedResultDetail?.updated_at).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Batch Results
            </Typography>
            {allResults.length > 0 && (
              <Chip 
                label={`${allResults.length.toLocaleString()} keywords`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ ml: 4 }}>
            <CopyableText text={batchId} label="Batch ID" variant="caption" />
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'row', gap: 0 }}>
        {/* Left Panel - Keyword List */}
        <Box
          sx={{
            width: '35%',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Status Filter Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={statusFilter}
              onChange={handleStatusFilterChange}
              variant="fullWidth"
              sx={{
                minHeight: '48px',
                '& .MuiTab-root': {
                  minHeight: '48px',
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              <Tab label="All" value="all" />
              <Tab label="Completed" value="completed" />
              <Tab label="Failed" value="failed" />
            </Tabs>
          </Box>

          {/* Search Bar and Page Size Selector */}
          <Box sx={{ p: 2, pb: 0, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <SearchInput
                onSearchChange={handleSearchChange}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
              
              {/* Page size selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Show:
                </Typography>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  size="small"
                  sx={{ minWidth: 70 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* Keyword List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px', gap: 2 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                  Loading keywords...
                </Typography>
              </Box>
            ) : filteredResults.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'No matching keywords found' : 'No results available'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {paginatedResults.map((result, index) => (
                  <React.Fragment key={result.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedResult?.id === result.id}
                        onClick={() => handleKeywordClick(result)}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.50',
                            borderLeft: 3,
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  fontWeight: selectedResult?.id === result.id ? 600 : 400,
                                  wordBreak: 'break-word'
                                }}
                              >
                                {result.keyword}
                              </Typography>
                              <Tooltip title={result.status === 'completed' ? 'Success' : 'Error'}>
                                {result.status === 'completed' ? (
                                  <SuccessIcon fontSize="small" color="success" />
                                ) : (
                                  <ErrorIcon fontSize="small" color="error" />
                                )}
                              </Tooltip>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < paginatedResults.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Pagination */}
          {filteredResults.length > 0 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {/* Page navigation buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                  title="Previous page"
                >
                  <PreviousIcon fontSize="small" />
                </IconButton>
                
                {/* Page numbers */}
                {(() => {
                  const pageNumbers = [];
                  const delta = 1; // Number of pages to show on each side of current page
                  
                  // Helper to add a page button
                  const addPageButton = (pageNum: number) => {
                    pageNumbers.push(
                      <Button
                        key={pageNum}
                        size="small"
                        variant={pageNum === page ? "contained" : "text"}
                        onClick={() => setPage(pageNum)}
                        sx={{ 
                          minWidth: 36, 
                          px: 1, 
                          py: 0,
                          minHeight: 'auto',
                          height: 'auto'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  };
                  
                  // Helper to add ellipsis
                  const addEllipsis = (key: string) => {
                    pageNumbers.push(
                      <Typography key={key} variant="body2" sx={{ px: 1, color: 'text.secondary' }}>
                        ...
                      </Typography>
                    );
                  };
                  
                  if (totalPages <= 7) {
                    // Show all pages if there are 7 or fewer
                    for (let i = 1; i <= totalPages; i++) {
                      addPageButton(i);
                    }
                  } else {
                    // Always show first page
                    addPageButton(1);
                    
                    // Calculate range around current page
                    const startPage = Math.max(2, page - delta);
                    const endPage = Math.min(totalPages - 1, page + delta);
                    
                    // Add left ellipsis if needed
                    if (startPage > 2) {
                      addEllipsis('ellipsis-left');
                    }
                    
                    // Show pages around current page
                    for (let i = startPage; i <= endPage; i++) {
                      addPageButton(i);
                    }
                    
                    // Add right ellipsis if needed
                    if (endPage < totalPages - 1) {
                      addEllipsis('ellipsis-right');
                    }
                    
                    // Always show last page
                    addPageButton(totalPages);
                  }
                  
                  return pageNumbers;
                })()}
                
                <IconButton
                  size="small"
                  onClick={handleNextPage}
                  disabled={!hasMore}
                  title="Next page"
                >
                  <NextIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Showing X - Y of Z */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, filteredResults.length)} of {filteredResults.length} {filteredResults.length === 1 ? 'keyword' : 'keywords'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Right Panel - Result Detail */}
        <Box
          sx={{
            width: '65%',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Loading overlay - doesn't replace content, just overlays it */}
          {isLoadingDetail && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                zIndex: 1000,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          {renderResultDetail()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BatchResultsDialog;

